import type { ButtonHTMLAttributes, ReactNode } from "react";
import { UiLink } from "./link";
import { cn, focusRing } from "./utils";

export type ButtonVariant = "primary" | "tonal" | "outline" | "soft" | "ghost" | "danger" | "danger-fill";
export type ButtonSize = "sm" | "md" | "lg";

const variants: Record<ButtonVariant, string> = {
  primary: "bg-blue text-white hover:bg-blue-dark",
  tonal: "bg-blue-tint text-blue-dark hover:bg-blue-container",
  outline: "border border-line bg-surface text-ink hover:bg-linen",
  soft: "bg-surface-header text-ink hover:bg-surface-low",
  ghost: "bg-transparent text-ink hover:bg-surface-header",
  danger: "border border-line bg-surface text-scarlet hover:bg-scarlet-tint",
  "danger-fill": "bg-scarlet text-white hover:brightness-95",
};

const sizes: Record<ButtonSize, string> = {
  sm: "h-[38px] px-3.5 rounded-[10px] text-sm gap-1.5",
  md: "h-11 px-4 rounded-field text-sm gap-2",
  lg: "h-[52px] px-5 rounded-field text-base gap-2.5",
};

export function buttonClasses({
  variant = "primary",
  size = "md",
  block = false,
  className,
}: {
  variant?: ButtonVariant;
  size?: ButtonSize;
  block?: boolean;
  className?: string;
} = {}) {
  return cn(
    "inline-flex items-center justify-center font-semibold whitespace-nowrap cursor-pointer select-none transition-colors",
    "disabled:cursor-not-allowed disabled:opacity-50",
    focusRing,
    variants[variant],
    sizes[size],
    block && "w-full",
    className,
  );
}

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  block?: boolean;
  icon?: ReactNode;
};

export function Button({
  variant,
  size,
  block,
  icon,
  className,
  children,
  type = "button",
  ...rest
}: ButtonProps) {
  return (
    <button type={type} className={buttonClasses({ variant, size, block, className })} {...rest}>
      {icon}
      {children}
    </button>
  );
}

export type ButtonLinkProps = {
  href: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  block?: boolean;
  icon?: ReactNode;
  className?: string;
  children?: ReactNode;
  "aria-label"?: string;
};

export function ButtonLink({
  href,
  variant,
  size,
  block,
  icon,
  className,
  children,
  ...rest
}: ButtonLinkProps) {
  return (
    <UiLink href={href} className={buttonClasses({ variant, size, block, className })} {...rest}>
      {icon}
      {children}
    </UiLink>
  );
}
